// 店舗ごとに言語別編集するテキストの共通型とヘルパー。
// 言語コードは Redux state.lang / i18n と一致させる（zh は大文字TW）。
export const LANG_CODES = ["ja", "en", "zh-TW", "vi"] as const;
export type LangCode = (typeof LANG_CODES)[number];
export type LocalizedText = { [K in LangCode]?: string };

// 指定言語の値を返す。未設定・空文字・null/undefined・レガシー単一文字列を安全に処理。
export function pickLocalized(
  value: LocalizedText | string | null | undefined,
  lang: string,
  fallback = ""
): string {
  if (value == null) return fallback;
  if (typeof value === "string") return value !== "" ? value : fallback;
  const v = value[lang as LangCode];
  return v != null && v !== "" ? v : fallback;
}

// pickLocalized と似るが「未設定(null/undefined)のみ fallback、空文字 "" は尊重」する版。
// depositExplanation のように「未編集なら初期文言(i18n)へフォールバックするが、
// 店舗が意図的に空欄にした場合はその空欄を維持する」用途で使う。
export function pickLocalizedOrDefault(
  value: LocalizedText | string | null | undefined,
  lang: string,
  fallback: string
): string {
  if (value == null) return fallback;
  if (typeof value === "string") return value; // レガシー単一文字列はそのまま（""含む）
  const v = value[lang as LangCode];
  return v == null ? fallback : v; // 未設定→fallback、""→""、値→値
}

// 既存の単一文字列を全4言語へ複製（マイグレーション後の挙動維持用 / 防御的正規化用）。
export function localizedFromLegacy(str: string | null | undefined): LocalizedText {
  const s = str ?? "";
  return { ja: s, en: s, "zh-TW": s, vi: s };
}

// 新規フィールドの空初期値。
export function emptyLocalized(): LocalizedText {
  return { ja: "", en: "", "zh-TW": "", vi: "" };
}

// 全言語が空（空白のみ含む）か未設定なら true。
export function isLocalizedEmpty(value: LocalizedText | null | undefined): boolean {
  if (value == null) return true;
  return LANG_CODES.every((l) => {
    const v = value[l];
    return v == null || v.trim() === "";
  });
}
